import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

// Code-split heavy / rarely-first routes so the public Landing + Login load fast.
const SignupCompany = lazy(() => import("@/pages/SignupCompany"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const PlatformAdmin = lazy(() => import("@/pages/PlatformAdmin"));
const CompanyAdmin = lazy(() => import("@/pages/CompanyAdmin"));
const EmployeePortal = lazy(() => import("@/pages/EmployeePortal"));

const PageLoader = () => <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "platform_admin") return <Navigate to="/platform" replace />;
  if (["company_admin", "hr", "accountant"].includes(user.role)) return <Navigate to="/company" replace />;
  return <Navigate to="/portal" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignupCompany />} />
              <Route element={<Protected><Layout /></Protected>}>
                <Route path="/app" element={<RoleHome />} />
                <Route path="/platform/*" element={<PlatformAdmin />} />
                <Route path="/company/*" element={<CompanyAdmin />} />
                <Route path="/portal/*" element={<EmployeePortal />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
